import Button from "@/components/forms/Button/Button";
import Flex from "@/components/Flex/Flex";
import { useLocation } from "wouter";
import { useUserContext } from "@/contexts/UserContext";
import { useInventoryContext } from "@/contexts/InventoryContext";
import { useEffect, useState } from "react";
import { NotificationType, useNotificationContext } from "@/contexts/NotificationContext";
import { API } from "@/lib/api";
import IconLogout from "@/components/icons/IconLogout";
import TextInput from "@/components/forms/TextInput/TextInput";
import Tooltip from "@/components/Tooltip";
import { resendVerifyEmail, verifyOTP } from "@/api/auth";
import { ErrorCode, UserRole, UserStatus } from "@boxem/core-api-schema";
import { ChangeEmailPage } from "../../ChangeEmailPage/ChangeEmailPage";
import LoginLayout from "../layout";

import styles from "./new-verify-email-page.module.css";
import { navigatePreserveSearchParams } from "@/utils/navigationUtils";


const intervalID = { id: null };
export default function NewVerifyEmailPage() {
  const [_, navigate] = useLocation();
  const { user, setUser } = useUserContext();
  const { clearInventory } = useInventoryContext();
  const { showNotification } = useNotificationContext();

  const [resendTimestamp, setResendTimestamp] = useState<Date>(null);
  const [resendTimeRemaining, setResendTimeRemaining] = useState<Date>(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOTP] = useState<string>("");
  const [isChangingEmail, setIsChangingEmail] = useState<boolean>(false);

  useEffect(() => {
    if (resendTimestamp) {
      clearInterval(intervalID.id);
      intervalID.id = setInterval(() => {
        const remaining = resendTimestamp.getTime() - new Date().getTime();
        if (remaining < 0) {
          setResendTimeRemaining(null);
          setResendTimestamp(null);
          return clearInterval(intervalID.id);
        }
        setResendTimeRemaining(new Date(remaining));
      }, 900);
    }
    return () => clearInterval(intervalID.id);
  }, [resendTimestamp])
  
  const handleResendConfirmationLink = () => {
    setResendTimestamp(new Date(new Date().getTime() + 60_000)); // 1 minute from now
    resendVerifyEmail().then(() => {
      showNotification("Confirmation link has been re-sent!\nPlease check your email to verify your account.", NotificationType.SUCCESS);
    })
    .catch((err) => {
      showNotification("Confirmation link re-send failed!\nPlease try again later.", NotificationType.ERROR);
    });
  };

  useEffect(() => {
    if (user?.emailVerified) {
      navigate("~/");
    }
  }, [user]);

  if (isChangingEmail) {
    return (
      <ChangeEmailPage onGoBack={() => setIsChangingEmail(false)} onEmailChanged={() => setIsChangingEmail(false)}/>
    );
  }

  const submit = (otp: string) => {
    setLoading(true);
    verifyOTP(otp)
      .then(() => {
        setUser({...user, status: user.status === UserStatus.EMAIL_VERIFICATION ? UserStatus.DOING_INITIAL_PAYMENT : user.status, emailVerified: true }, false);

        if (user.isSubscribed || user.role === UserRole.ASSISTANT) {
          // we're verifying the email as part of a 'change-email' request,
          // or as an assistant logging in for the first time.
          navigate("/"); 
        } else {
          // we're verifying the email as part of the sign-up flow, proceed to payment:
          navigatePreserveSearchParams(`/auth/payment-information`, navigate);
        }
      })
      .catch(e => {
        switch (e?.[0]?.type) {
          case ErrorCode.SIGNUP_OTP_EXPIRED:
            showNotification("Verification code has expired!")
            break;
          default:
            showNotification("Failed to verify! You can try again later, or re-send the email.");
            break;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <LoginLayout>
      <Flex className={styles.primaryColumn} direction="column" justify="space-between" gap={4}>
        <Flex direction="column" gap={4}>
          <Flex gap={4} direction="column">
            <h3>Confirm Your Email</h3>
            <p>Please check your email inbox for a verification code to activate your account.</p>
          </Flex>
          <p>
            If you haven’t received an email within 5 minutes, check your spam, or&nbsp;
            <Button 
              type="link"
              disabled={!!resendTimeRemaining}
              onClick={handleResendConfirmationLink}
            >
              resend the verification email{resendTimeRemaining ? ` (${resendTimeRemaining.getSeconds()})` : undefined}.
            </Button>
          </p>

          <Flex direction="column" gap={1}>
            <label htmlFor="otp">Verification Code:</label>
            <TextInput
              inputId="otp"
              placeholder="Enter your 6-digit verification code here"
              maxLength={6}
              value={otp}
              onChange={v => {
                const currentOtp = otp;
                const trimmedLength = v.trim().length;
                if (trimmedLength === 6 && currentOtp.length < trimmedLength) {
                  submit(v.trim()); // immediately submit if the customer pastes in the otp
                }
                setOTP(v);
              }}
            />

            <Tooltip
              title={otp.length !== 6 ? "Verification code must be exactly 6 digits" : ""}
            >
              <Button
                style={{marginTop:"16px"}}
                type="primary"
                disabled={otp.length !== 6 || loading}
                onClick={() => submit(otp)}
              >
                Submit
              </Button>
            </Tooltip>
          </Flex>

          <Button
            type="text"
            onClick={() => {
              setIsChangingEmail(true);
            }}
          >
            <span className={styles.orange}>Change Email</span>
          </Button>
        </Flex>

        <Button
          type="secondary"
          onClick={() => {
            clearInventory();
            navigate("~/auth/login");
            API.logout();
          }}
        >
          <IconLogout />
          Logout
        </Button> 
      </Flex>
    </LoginLayout>
  );
}
